import React from 'react';
import styled from 'styled-components';

import { useQuery } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';

import useConfig from 'app/config';
import GenericPage from 'app/ui/components/layouts/generic-page';

import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import ResourceSections from 'app/ui/components/molecules/resource-sections';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import ResourceNavigation from 'app/ui/components/molecules/resource-navigation';
import MoreResourceContent from 'app/ui/components/molecules/more-resource-content';
import AcademyBanner from 'app/ui/components/molecules/academy-banner';

import { ResourcePage as RESOURCE } from 'app/pages/resource/resource.gql';
import type { ResourcePageQuery, ResourcePageQueryVariables } from 'app/types/graphql';
import Button from 'src/app/ui/components/atoms/button';
import { useAppState } from 'src/state';

const BreadcrumbWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  display: none;

  ${({ theme: { media, spacing } }) =>
    `${media.medium(
      () => `
       margin: ${spacing(14)} 0 ${spacing(6)} 0;
       display: inline-flex;
       `,
    )}`}
`;

const CTALink = styled.a`
  ${({ theme: { spacing, media } }) => `
   width: 100%;
   margin: ${spacing(7)} 0 ${spacing(3)} 0;
  
  ${media.medium(
    () => `
  width: auto;
  margin: ${spacing(14)} 0 ${spacing(6)} 0;
  `,
  )}
  `}
`;

const CTAButton = styled(Button)`
  width: 100%;
`;

type ResourcePageProps = {
  resourceId: string;
  userId: number;
  resourceListId?: string;
};

const ResourcePage: React.FC<ResourcePageProps> = ({ resourceId, userId, resourceListId }: ResourcePageProps) => {
  const { config } = useConfig();
  const resourceListsEnabled = config.REACT_APP_RESOURCE_LISTS_ENABLED;

  const { data, loading, error } = useQuery<ResourcePageQuery, ResourcePageQueryVariables>(RESOURCE, {
    variables: { id: resourceId, userId },
  });

  if (loading || !data?.resource || !data?.user) {
    return <Loading />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const {
    resource: {
      title,
      content,
      image,
      sections = [],
      id,
      ctaLink,
      ctaLabel,
      ctaAppendPortalGymCodeQueryString,
      showAcademyPromo,
    },
    user: { gym, portalUserCode },
  } = data;

  const portalGymCode = gym?.portalGymCode;

  const ctaLinkWithPortalGymCode =
    ctaAppendPortalGymCodeQueryString && ctaLink && (portalGymCode || portalUserCode)
      ? (() => {
          const url = new URL(ctaLink, window.location.origin);
          if (portalGymCode) {
            url.searchParams.set('code', portalGymCode);
          } else if (portalUserCode) {
            url.searchParams.set('userCode', portalUserCode);
          }
          return url.toString();
        })()
      : ctaLink;

  const hasCTA = ctaLinkWithPortalGymCode && ctaLabel;

  return (
    <>
      <GenericPage pageTitle={`Resource - ${title}`}>
        <ResourceHeader title={title} content={content} imageUrl={image?.url || image?.signedUrl} />
        <BreadcrumbWrapper>
          <StyledBreadcrumb contentTitle={title} />
          {hasCTA && (
            <CTALink href={ctaLinkWithPortalGymCode} target="_blank">
              <CTAButton label={ctaLabel} />
            </CTALink>
          )}
        </BreadcrumbWrapper>
        <ResourceSections sections={sections || []} resourceId={id} />

        {resourceListsEnabled && resourceListId && (
          <ResourceNavigation resourceListId={resourceListId} currentResourceId={resourceId} />
        )}

        {resourceListsEnabled && <MoreResourceContent limit={3} />}
        {showAcademyPromo && <AcademyBanner />}
      </GenericPage>
    </>
  );
};

const Resource: React.FC = () => {
  const { id: resourceId } = useParams();
  const userId = useAppState(({ auth }) => auth.userId);
  const location = useLocation();

  const resourceListId = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('resourceListId') || undefined;
  }, [location.search]);

  if (!resourceId || !userId) {
    return <ErrorComponent />;
  }

  return <ResourcePage resourceId={resourceId} resourceListId={resourceListId} userId={userId} />;
};

export default Resource;
